import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import EventBusyIcon from "@mui/icons-material/EventBusy";

import {
  Popover, Box, Typography, Grid, Skeleton,
} from "@mui/material";

import {
  setSelectedEventOrStdResType,
  updateEventDates,
  updateEventOrStdTicketTypesData,
  updateReservationType,
  updateSelectedDate,
  updateStdDates,
} from "../../store/slice/Ticketing.slice";
import { UsePopoverPosition } from "../../hooks/UsePopoverPosition";

const EventComponent = ({ Ref, isOpen, handleClose, loading }) => {
  const { css } = useSelector((store) => store?.widgetSettings);
  const eventCss = css?.find((item) => item?.cssTabFieldsGroupName === "Event")?.fields;

  const [imageValidity, setImageValidity] = useState({});

  const dispatch = useDispatch();
  const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;

  const { EventorStdTicketData, reservType, languageWords } = useSelector(
    (store) => store?.Ticketing,
  );

  // ── Shared positioning (identical behaviour to ReservationType) ───────────
  const {
    anchorOriginVert,
    transformOriginVert,
    paperSx,
    mobileOverrideProps,
  } = UsePopoverPosition(Ref, isOpen, 400);

  const isEmpty =
    !Array.isArray(EventorStdTicketData) || EventorStdTicketData.length === 0;

  const handleSelectTicket = (item) => {
    dispatch(updateStdDates([]));
    dispatch(updateEventDates([]));
    dispatch(updateSelectedDate(""));
    dispatch(updateReservationType(item));
    dispatch(updateEventOrStdTicketTypesData([]));
    dispatch(setSelectedEventOrStdResType({}));
    handleClose();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Popover
      open={isOpen}
      anchorEl={Ref?.current}
      onClose={handleClose}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      {...mobileOverrideProps}       
      PaperProps={{
        sx: {
          ...paperSx,                 
          backgroundColor: eventCss?.EventMainBgClr,
        },
      }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
    >
      {loading ? (
        <Box p={3} display="flex" flexDirection="column" gap={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: eventCss?.EventMainBgClr,
            width: "100%",
            p: 3,
            pb: 3,
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          {isEmpty ? (
            <Box
              sx={{
                minHeight: 100,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "text.secondary",
              }}
            >
              <EventBusyIcon sx={{ fontSize: 56, mb: 1, opacity: 0.5 }} />
              <Typography variant="h6" fontWeight={600}>
                {languageWords?.noEventsAvailable || "No events available"}
              </Typography>
            </Box>
          ) : (
            EventorStdTicketData.map((item) => {
              const isActive = reservType?.eventTicketId === item?.eventTicketId;

              return (
                <Box
                  key={item?.eventTicketId}
                  onClick={() => handleSelectTicket(item)}
                  sx={{
                    position: "relative",
                    display: "flex",
                    minHeight: "150px",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: "0.5rem",
                    backgroundColor: isActive
                      ? eventCss?.EventItemHighlightBgClr
                      : eventCss?.EventItemBgClr,
                    border: `1px solid ${eventCss?.EventBorderClr}`,
                    p: 1,
                    borderRadius: "8px",
                    cursor: "pointer",
                    width: "100%",
                    transition: "all 0.3s ease-in-out",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: "0px 6px 18px rgba(0,0,0,0.2)",
                    },
                    "@media (hover: none)": {
                      "&:hover": { transform: "none", boxShadow: "none" },
                      "&:active": { opacity: 0.85 },
                    },
                  }}
                >
                  <Grid container spacing={2}>
                    {item?.imagePath &&
                      imageValidity[item.eventTicketId] !== false && (
                        <Grid item xs={12} sm={4} md={4}>
                          <Box
                            sx={{
                              width: "100%",
                              height: { xs: 180, sm: "100%" },
                              minHeight: { sm: 120 },
                              borderRadius: 2,
                              overflow: "hidden",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                              "&:hover img": { transform: "scale(1.05)" },
                            }}
                          >
                            <img
                              src={`${IMG_URL}/upload/Event${item.imagePath.replace(/\\/g, "/")}`}
                              alt="event"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                                transition: "transform 0.3s ease",
                              }}
                              onLoad={() =>
                                setImageValidity((p) => ({ ...p, [item.eventTicketId]: true }))
                              }
                              onError={() =>
                                setImageValidity((p) => ({ ...p, [item.eventTicketId]: false }))
                              }
                            />
                          </Box>
                        </Grid>
                      )}

                    <Grid item xs={12} sm={8} md={imageValidity[item.eventTicketId] ? 8 : 12}>
                      <Typography
                        fontSize={14}
                        fontWeight={600}
                        sx={{
                          color: isActive
                            ? eventCss?.EventTitleHighlightClr
                            : eventCss?.EventTitleClr,
                        }}
                      >
                        {item?.eventTicketName}
                      </Typography>

                      {(item?.address || item?.eventLocation) && (
                        <Typography
                          fontSize={13}
                          sx={{
                            color: isActive
                              ? eventCss?.EventHighlightAdressClr
                              : eventCss?.EventAdressClr,
                            fontWeight: "bold",
                          }}
                        >
                          {[item.address, item.eventLocation].filter(Boolean).join(" | ")}
                        </Typography>
                      )}

                      <Typography
                        fontSize={13}
                        sx={{
                          color: isActive
                            ? eventCss?.EventHighlightAdressClr
                            : eventCss?.EventAdressClr,
                          fontWeight: "bold",
                        }}
                      >
                        {item?.eventDateAndTime}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: 13,
                          mt: 1,
                          color: isActive
                            ? eventCss?.EventHighlightAdressClr
                            : eventCss?.EventAdressClr,
                        }}
                      >
                        {item?.eventTicketInfo}
                      </Typography>

                      <Typography
                        fontSize={13}
                        sx={{
                          color: isActive
                            ? eventCss?.EventHighlightInfoClr
                            : eventCss?.EventInfoClr,
                        }}
                      >
                        {item?.eventInfo}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              );
            })
          )}
        </Box>
      )}
    </Popover>
  );
};

export default EventComponent;
